// Guia "Como usar" (texto 08): peças das páginas do guia — texto com os nomes reais dos botões, passos com ✓,
// "▶ Mostrar na tela", gestos animados, perguntas comuns e a página inteira (seções recolhíveis).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Pressable, Text, View, type TextStyle } from 'react-native';
import { C, alpha } from '../theme';
import { t } from '../../lib/i18n';
import { Recolhivel } from '../Recolhivel';
import { setAberto } from '../../lib/recolher';
import { useCaixas } from '../../lib/caixas';
import { BotaoAcao } from '../ajustes/Linhas';
import { Gesto } from './Gesto';
import { SECOES_ABA, passosComMarca, plano, rico, type Pagina, type Passo, type Pergunta, type Secao } from '../../lib/guia/conteudo';
import { focoUsado, mostrarNaTela, useFoco } from '../../lib/guia/destaque';
import { limparMarcas, marcarPasso, useFeitos } from '../../lib/guia/marcas';

/** Rolar a tela de Ajustes até um ponto (y na janela). */
export type Rolar = (yNaJanela: number) => void;

// Chaves das seções (o app lembra o que ficou aberto, lib/recolher.ts)
export const chaveSecao = (pagina: string, secao: string) => `guia:sec:${pagina}:${secao}`;
export const chavePergunta = (pagina: string, id: string) => `guia:perg:${pagina}:${id}`;

/** Texto do guia: {t:Botão} com o nome real (negrito) e **negrito**. */
export function TextoGuia({ s, estilo }: { s?: string; estilo?: TextStyle }) {
  const partes = rico(s);
  if (!partes.length) return null;
  return (
    <Text style={[{ color: C.text, fontSize: 14, lineHeight: 20 }, estilo]}>
      {partes.map((p, i) => (p.botao || p.negrito ? <Text key={i} style={{ fontWeight: '800' }}>{p.texto}</Text> : p.texto))}
    </Text>
  );
}

export const textoConstrucao = () => '🚧 ' + t('Guia em construção');
export function AvisoConstrucao() {
  return <Text style={{ color: C.sub, fontSize: 13, paddingVertical: 6 }}>{textoConstrucao()}</Text>;
}

/** "▶ Mostrar na tela". */
export function BotaoMostrar({ alvo, texto, pagina, secao, passo, pergunta, cor }:
  { alvo: string; texto?: string; pagina: string; secao?: string; passo?: string; pergunta?: string; cor: string }) {
  return (
    <View style={{ flexDirection: 'row' }}>
      <Pressable onPress={() => mostrarNaTela(alvo, { texto: plano(texto), volta: { pagina, secao, passo, pergunta } })} hitSlop={6}
        accessibilityRole="button" style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 10,
          borderRadius: 14, borderWidth: 1, borderColor: cor, backgroundColor: pressed ? alpha(cor, 0.12) : 'transparent' })}>
        <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>▶ {t('Mostrar na tela')}</Text>
      </Pressable>
    </View>
  );
}

/** Um passo: número, frase curta, gesto animado, "Mostrar na tela" e a caixinha ✓ (já fiz). */
function PassoItem({ p, n, pagina, secao, cor, feito, comMarca, realce, guardar, tamGesto }:
  { p: Passo; n: number | null; pagina: string; secao: string; cor: string; feito: boolean; comMarca: boolean; realce: boolean;
    guardar: (id: string, v: View | null) => void; tamGesto: 'p' | 'm' }) {
  return (
    <View ref={v => guardar(p.id, v)} collapsable={false}
      style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start', paddingVertical: 6, paddingHorizontal: 6, marginHorizontal: -6, borderRadius: 10,
        backgroundColor: realce ? alpha(cor, 0.18) : 'transparent' }}>
      <Text style={{ color: n ? cor : C.sub, fontWeight: '800', minWidth: 18, fontSize: 14, lineHeight: 20 }}>{n ? `${n}.` : '•'}</Text>
      <View style={{ flex: 1, gap: 6 }}>
        <TextoGuia s={p.texto} estilo={feito ? { color: C.sub } : undefined} />
        {!!p.gesto && <Gesto id={p.gesto} tam={tamGesto} cor={cor} />}
        {!!p.mostrar && <BotaoMostrar alvo={p.mostrar} texto={p.texto} pagina={pagina} secao={secao} passo={p.id} cor={cor} />}
      </View>
      {comMarca && (
        <Pressable onPress={() => marcarPasso(pagina, p.id, !feito)} hitSlop={10} accessibilityRole="checkbox" accessibilityState={{ checked: feito }}
          accessibilityLabel={t('Já fiz')}
          style={{ width: 24, height: 24, borderRadius: 6, borderWidth: 2, borderColor: feito ? cor : C.mute, backgroundColor: feito ? cor : 'transparent',
            alignItems: 'center', justifyContent: 'center', marginTop: -1 }}>
          {feito && <Text style={{ color: '#fff', fontWeight: '900', fontSize: 14, lineHeight: 16 }}>✓</Text>}
        </Pressable>
      )}
    </View>
  );
}

/** Uma pergunta comum: recolhida; aberta mostra a resposta e, se houver, "Mostrar na tela". */
function PerguntaItem({ q, pagina, secao, cor, realce, guardar }:
  { q: Pergunta; pagina: string; secao?: string; cor: string; realce: boolean; guardar: (id: string, v: View | null) => void }) {
  return (
    <View ref={v => guardar(q.id, v)} collapsable={false} style={{ borderRadius: 10, paddingHorizontal: 6, marginHorizontal: -6, backgroundColor: realce ? alpha(cor, 0.18) : 'transparent' }}>
      <Recolhivel titulo={plano(q.p)} chave={chavePergunta(pagina, q.id)} inicial={false}>
        <View style={{ gap: 8, paddingBottom: 6 }}>
          <TextoGuia s={q.r} estilo={{ color: C.sub }} />
          {!!q.mostrar && <BotaoMostrar alvo={q.mostrar} texto={q.r} pagina={pagina} secao={secao} pergunta={q.id} cor={cor} />}
        </View>
      </Recolhivel>
    </View>
  );
}

/** Abre a seção pedida (busca ou "Voltar ao guia"), rola até o passo e o realça por alguns segundos. */
function useFocoNaPagina(pagina: Pagina, rolar?: Rolar) {
  const foco = useFoco();
  const refs = useRef(new Map<string, View | null>());
  const [realce, setRealce] = useState<string | null>(null);
  const guardar = (id: string, v: View | null) => { refs.current.set(id, v); };
  useEffect(() => {
    if (!foco || foco.pagina !== pagina.id) return;
    focoUsado(foco.n);
    const sec = foco.secao ?? pagina.secoes.find(s => s.passos?.some(p => p.id === foco.passo) || s.perguntas?.some(q => q.id === foco.pergunta))?.id;
    if (sec) setAberto(chaveSecao(pagina.id, sec), true);
    if (foco.pergunta) setAberto(chavePergunta(pagina.id, foco.pergunta), true);
    const item = foco.passo ?? foco.pergunta ?? null;
    setRealce(item);
    const i = setTimeout(() => {
      const v = (item && refs.current.get(item)) || null;
      if (v && rolar) v.measureInWindow((_x, y) => rolar(y));
    }, 350);
    const f = setTimeout(() => setRealce(null), 3000);
    return () => { clearTimeout(i); clearTimeout(f); };
  }, [foco?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  return { realce, guardar };
}

/** Progresso dos ✓ da página: "3 de 7 passos feitos". */
export function useProgresso(pagina: Pagina) {
  const feitos = useFeitos(pagina.id);
  const ids = passosComMarca(pagina).map(p => p.id);
  const n = ids.filter(id => feitos.includes(id)).length;
  return { n, total: ids.length, feitos, texto: ids.length ? t('{n} de {total} passos feitos', { n, total: ids.length }) : '' };
}

/** Texto 29 (item 5): uma parte no formato "Quero… → faça…": o título fica DENTRO do cartão (recolhível), sem números
 *  nem quadradinhos de marcar; a animação aparece inteira logo abaixo da linha que ela mostra. */
function SecaoQuero({ s, pagina, cor, realce, guardar }: { s: Secao; pagina: string; cor: string; realce: string | null; guardar: (id: string, v: View | null) => void }) {
  const cx = useCaixas();
  return (
    <View style={{ backgroundColor: C.card, borderRadius: cx.raio(14), paddingHorizontal: cx.padH(14), paddingVertical: cx.pad(4), borderWidth: 1, borderColor: C.line }}>
      <Recolhivel chave={chaveSecao(pagina, s.id)} inicial={!!s.aberta} titulo={s.titulo} gap={4} estiloTitulo={{ fontSize: 15, fontWeight: '800' }}>
        <View style={{ gap: 10, paddingBottom: 10 }}>
          {!!s.texto && <TextoGuia s={s.texto} />}
          {(s.passos ?? []).map(p => (
            <View key={p.id} ref={v => guardar(p.id, v)} collapsable={false}
              style={{ gap: 8, borderRadius: 10, paddingHorizontal: 6, marginHorizontal: -6, paddingVertical: 2, backgroundColor: realce === p.id ? alpha(cor, 0.18) : 'transparent' }}>
              <TextoGuia s={p.texto} estilo={{ fontSize: 15, lineHeight: 22 }} />
              {!!(p.gestos?.length || p.gesto) && (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center', paddingVertical: 4 }}>
                  {(p.gestos ?? [p.gesto!]).map(g => <Gesto key={g} id={g} tam="g" cor={cor} />)}
                </View>
              )}
              {!!p.mostrar && <BotaoMostrar alvo={p.mostrar} texto={p.texto} pagina={pagina} secao={s.id} passo={p.id} cor={cor} />}
            </View>
          ))}
        </View>
      </Recolhivel>
    </View>
  );
}

/** Uma seção da página (recolhível): texto, passos numerados (ou dicas com •) e perguntas. */
function SecaoGuia({ s, pagina, cor, feitos, realce, guardar, tamGesto }:
  { s: Secao; pagina: string; cor: string; feitos: string[]; realce: string | null; guardar: (id: string, v: View | null) => void; tamGesto: 'p' | 'm' }) {
  const cx = useCaixas();
  const dicas = s.id === SECOES_ABA.dicas;
  const vazia = !s.texto && !s.passos?.length && !s.perguntas?.length;
  return (
    <Recolhivel visual="cartao" chave={chaveSecao(pagina, s.id)} inicial={!!s.aberta} titulo={s.titulo} gap={6}>
      <View style={{ backgroundColor: C.card, borderRadius: cx.raio(14), paddingHorizontal: cx.padH(14), paddingVertical: cx.pad(10), gap: 4 }}>
        {!!s.texto && <TextoGuia s={s.texto} />}
        {(s.passos ?? []).map((p, i) => (
          <PassoItem key={p.id} p={p} n={dicas ? null : i + 1} pagina={pagina} secao={s.id} cor={cor} feito={feitos.includes(p.id)} comMarca={!dicas}
            realce={realce === p.id} guardar={guardar} tamGesto={tamGesto} />
        ))}
        {(s.perguntas ?? []).map(q => <PerguntaItem key={q.id} q={q} pagina={pagina} secao={s.id} cor={cor} realce={realce === q.id} guardar={guardar} />)}
        {vazia && <AvisoConstrucao />}
      </View>
    </Recolhivel>
  );
}

/** Página do guia: progresso, seções e "Limpar marcações" no fim. topo: o que vem antes das seções (ex.: "Abrir esta aba"). */
export function PaginaGuia({ pagina, cor, rolar, topo, depois, semProgresso, tamGesto = 'p' }:
  { pagina: Pagina; cor: string; rolar?: Rolar; topo?: ReactNode; depois?: ReactNode; semProgresso?: boolean; tamGesto?: 'p' | 'm' }) {
  const { realce, guardar } = useFocoNaPagina(pagina, rolar);
  const prog = useProgresso(pagina);
  if (pagina.formato === 'quero') return (<>
    {topo}
    {!!pagina.cabecalho && <Text style={{ color: C.text, fontSize: 18, fontWeight: '800', marginTop: 4 }}>{plano(pagina.cabecalho)}</Text>}
    {pagina.secoes.map(s => <SecaoQuero key={s.id} s={s} pagina={pagina.id} cor={cor} realce={realce} guardar={guardar} />)}
    {!!pagina.rodape && <TextoGuia s={pagina.rodape} estilo={{ color: C.sub }} />}
    {depois}
    {/* REGRAS 10.5: o + e a barra de baixo não cobrem o fim do texto */}
    <View style={{ height: 120 }} />
  </>);
  return (<>
    {topo}
    {!semProgresso && !!prog.total && <Text style={{ color: prog.n === prog.total ? C.ok : C.sub, fontSize: 13, fontWeight: '700' }}>{prog.n === prog.total ? '✓ ' : ''}{prog.texto}</Text>}
    {pagina.secoes.map(s => <SecaoGuia key={s.id} s={s} pagina={pagina.id} cor={cor} feitos={prog.feitos} realce={realce} guardar={guardar} tamGesto={tamGesto} />)}
    {depois}
    {!!prog.total && <BotaoAcao cor={cor} label={t('Limpar marcações')} disabled={!prog.n} onPress={() => limparMarcas(pagina.id)} />}
  </>);
}

/** Lista de perguntas comuns (a geral, na tela Como usar). */
export function ListaPerguntas({ pagina, perguntas, cor, rolar }: { pagina: string; perguntas: Pergunta[]; cor: string; rolar?: Rolar }) {
  const pag: Pagina = { id: pagina, titulo: '', secoes: [{ id: 'perguntas', titulo: '', perguntas }] };
  const { realce, guardar } = useFocoNaPagina(pag, rolar);
  if (!perguntas.length) return <AvisoConstrucao />;
  return <View style={{ gap: 2 }}>{perguntas.map(q => <PerguntaItem key={q.id} q={q} pagina={pagina} cor={cor} realce={realce === q.id} guardar={guardar} />)}</View>;
}
